"use client";

import EmptyState from "@/components/portfolio/empty-state";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { formatMonetaryValue } from "@/utils/utils";
import { BigMath } from "@yn/utils";
import * as React from "react";

interface CancelSummary {
    buyQty: number;
    buyValue: number;
    sellQty: number;
    sellValue: number;
}

interface CancelAllConfirmDialogProps {
    open: boolean;
    onOpenChange: ( open: boolean ) => void;
    summary: CancelSummary;
    onConfirm: () => void;
    isLoading?: boolean;
    isLoadingStats?: boolean;
}

export function CancelAllConfirmDialog(
    { open, onOpenChange, summary, onConfirm, isLoading = false, isLoadingStats = false }: CancelAllConfirmDialogProps,
) {
    const hasBuy = BigMath.gt( summary.buyQty, 0 );
    const hasSell = BigMath.gt( summary.sellQty, 0 );

    const handleCancel = () => {
        onOpenChange( false );
    };

    const handleConfirm = () => {
        onConfirm();
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="!max-w-[270px] p-0 gap-0 overflow-hidden rounded-[12px] [&_*[data-slot=dialog-close]]:hidden">
                <div className="flex flex-col gap-4 p-4">
                    <DialogTitle className="text-base font-bold text-t1 text-center leading-[normal]">
                        Cancel the order
                    </DialogTitle>

                    <div className="flex flex-col gap-3 w-full justify-center">
                        {isLoadingStats
                            ? (
                                <div className="flex flex-col gap-3 w-full py-[10px]">
                                    <div className="flex flex-col gap-2">
                                        <div className="flex justify-between">
                                            <div className="w-10 h-4 rounded skeleton" />
                                            <div className="w-20 h-4 rounded skeleton" />
                                        </div>
                                        <div className="flex justify-between">
                                            <div className="w-15 h-[14px] rounded skeleton" />
                                            <div className="w-25 h-[14px] rounded skeleton" />
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <div className="flex justify-between">
                                            <div className="w-10 h-4 rounded skeleton" />
                                            <div className="w-20 h-4 rounded skeleton" />
                                        </div>
                                        <div className="flex justify-between">
                                            <div className="w-15 h-[14px] rounded skeleton" />
                                            <div className="w-25 h-[14px] rounded skeleton" />
                                        </div>
                                    </div>
                                </div>
                            )
                            : (
                                <div className="py-1 flex flex-col gap-3">
                                    {hasBuy && (
                                        <div className="flex flex-col gap-1 w-full">
                                            <div className="flex items-center justify-between w-full">
                                                <span className="text-sm font-semibold text-[#00bf85] leading-[normal] capitalize">
                                                    Buy
                                                </span>
                                                <span className="text-sm font-semibold text-t1 leading-[normal] capitalize">
                                                    {BigMath.toFormat( summary.buyQty, 0 )} qty
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between w-full">
                                                <span className="text-xs font-medium text-t3 leading-[normal]">
                                                    Invested:
                                                </span>
                                                <span className="text-sm font-semibold text-yes leading-[normal] text-right">
                                                    {formatMonetaryValue( summary.buyValue )}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {hasSell && (
                                        <div className="flex flex-col gap-1 w-full">
                                            <div className="flex items-center justify-between w-full">
                                                <span className="text-sm font-semibold text-no leading-[normal] capitalize">
                                                    Sell
                                                </span>
                                                <span className="text-sm font-semibold text-t1 leading-[normal] capitalize">
                                                    {BigMath.toFormat( summary.sellQty, 0 )} qty
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between w-full">
                                                <span className="text-xs font-medium text-t2 leading-[normal]">
                                                    Sell Amount:
                                                </span>
                                                <span className="text-sm font-semibold text-yes leading-[normal] text-right">
                                                    {formatMonetaryValue( summary.sellValue )}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {!hasBuy && !hasSell && (
                                        <EmptyState
                                            text="No pending orders to cancel"
                                            className="h-auto py-[10px]"
                                            textClassName="text-sm"
                                        />
                                    )}
                                </div>
                            )}
                    </div>
                </div>

                <div className="flex flex-row border-t border-stroke">
                    <button
                        type="button"
                        onClick={handleCancel}
                        disabled={isLoading}
                        className="flex-1 h-12 text-base font-medium text-t3 bg-surface-card transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-r border-stroke
                         flex items-center justify-center"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isLoading || isLoadingStats}
                        className="flex-1 h-12 text-base font-semibold text-no bg-surface-card transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {isLoading && (
                            <div className="w-4 h-4 border-2 border-no border-t-transparent rounded-full animate-spin" />
                        )}
                        Confirm
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
